'use client';
import {
  FC,
  useMemo,
  SyntheticEvent,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useRouter } from 'next/navigation';

import { Button, Heading, Svg, Text } from '@/components';
import { OrderSummary } from '@/layouts/Order';
import { useCart, USERS_TYPES } from '@/context/cartContext';
import { useUser } from '@/context/userContext';

import CartProductItem from '../components/CartProductItem';
import { RETURN_EXCHANGE_POLICY } from '../constants/RefundExchangePolicy';
import { getCartBasedCategories } from '@/utils/helper';
import { http, endpoints, storage, logger } from '@/config';
import CartRecommendations from './RecommendedSection';
import { useCleverTap } from '@/context/clevertapContext';
import { getCartSkus } from '@/utils/helper';
import { twMerge } from 'tailwind-merge';
import { DELIVERY_CARD_PARAM_OPTIONS } from '@/features/Checkout/constants/delivery';
import { useLocation } from '@/context/locationContext';
import EditIcon from '../../../../public/images/global/EditIcon.svg';
import Image from 'next/image';
import { LocationPinIcon } from '@/icons';
import { HYDRABAD_STORE_CODE, REVALIDATE_TIME } from '@/constants';
import { DeliveryMethodsPropTypes } from '@/features/Checkout/sections/SectionHomeDelivery';
import PolicySection from '@/sections/Policy';
import { useLoader } from '@/context/loaderContext';
import DeliveryProgress from './DeliveryProgress';
import { Payment } from '@/models/order';
import { ShippingConfig } from '../type/types';

export enum DeliveryMethodsTypeEnum {
  'FLATRATE' = 'flatrate',
  'BESTWAY' = 'bestway',
  'FREESHIPPING' = 'freeshipping',
}

const Loader = () => (
  <div className='h-6 w-1/6 loading-color-transition rounded-2xl' />
);

const Loading = () => {
  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-3 items-center gap-3 px-3'>
        <div className='base-loading h-32 w-full bg-gray-300' />
        <div className='col-span-2 space-y-2 px-3'>
          <div className='base-loading w-32 bg-gray-300' />
          <div className='base-loading bg-gray-300' />
          <div className='base-loading w-12 bg-gray-300' />
        </div>
      </div>
    </div>
  );
};

const SectionCart: FC = () => {
  const {
    movDisable,
    movAmount,
    cartItemsMap,
    cartItems,
    cartValue,
    paymentDetails,
    toggleCartSidebar,
    loadingRecommended,
    fetchUpdateCart,
    cartIsLoading,
  } = useCart();
  const { pushEvent } = useCleverTap();
  const { setLoading } = useLoader();
  const { deliveryType, setShowDeliveryModal } = useLocation();
  const [slabFreeShipping, setSlabFreeShipping] = useState<ShippingConfig[]>(
    []
  );
  const [expressStandard, setExpressStandard] = useState(false);

  const {
    userDetails,
    setShowLoginModal,
    setLoginModalMessage,
    setLoginEventMetadata,
    setAsGuest,
    setLoginRedirect,
  } = useUser();
  const router = useRouter();
  const defaultValue =
    deliveryType === 'home'
      ? DELIVERY_CARD_PARAM_OPTIONS.homeDelivery
      : DELIVERY_CARD_PARAM_OPTIONS.pickupFromStore;
  const serviceableString = storage.get('serviceable');
  const [deliveryMethods, setDeliveryMethods] = useState<
    DeliveryMethodsPropTypes[] | null
  >(null);

  const [cartDisableErrorMessage, setCartDisableErrorMessage] =
    useState<string>();
  const [hasTrackedCartView, setHasTrackedCartView] = useState(false);
  const [isSlabShippingLoaded, setIsSlabShippingLoaded] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const updateTimeoutRef = useRef<NodeJS.Timeout>(null);

  const shippingAddress = {
    city: storage.get('city'),
    country_id: 'IN',
    region: storage.get('state'),
    street: [storage.get('addressDescription')],
  };
  const stringifiedShippingAddress = JSON.stringify(shippingAddress);

  const onSubmit = async (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (cartDisableErrorMessage) {
      return;
    }

    if (defaultValue === DELIVERY_CARD_PARAM_OPTIONS.homeDelivery) {
      if (storage.get('serviceable') === 'true') {
        cleverTapCheckoutStarted();
        if (userDetails?.id) {
          // Always proceed to checkout as guest (no login popup)
          toggleCartSidebar(false);
          router.push(`/checkout/${defaultValue.addressParam}/?query=address`);
        } else {
          setLoginEventMetadata({
            source: 'Checkout Button',
          });
          setLoginModalMessage(
            'Login using your mobile number to complete writing your food stories'
          );
          setLoginRedirect(
            `/checkout/${defaultValue.addressParam}/?query=address`
          );
          setAsGuest(true);
          setShowLoginModal(true);
        }
      }
    } else {
      cleverTapCheckoutStarted();
      if (userDetails?.id) {
        // Always proceed to checkout as guest (no login popup)
        toggleCartSidebar(false);
        router.push(`/checkout/${defaultValue.addressParam}/?query=address`);
      } else {
        setLoginEventMetadata({
          source: 'Checkout Button',
        });
        setLoginModalMessage(
          'Login using your mobile number to complete writing your food stories'
        );
        setLoginRedirect(
          `/checkout/${defaultValue.addressParam}/?query=address`
        );
        setAsGuest(true);
        setShowLoginModal(true);
      }
    }
  };

  const updatedCartItems = useMemo(() => {
    const nonGiftBoxItems = cartItems?.filter(item =>
      !!item?.extension_attributes?.gift_box_item_id &&
      item?.extension_attributes?.is_gift_box !== '1' &&
      item?.extension_attributes?.is_greeting_card !== 1
        ? false
        : true
    );
    const categorizedItems = getCartBasedCategories(nonGiftBoxItems);

    // Sort items within each category based on in_stock status
    const sortItemsByStock = (items: any[]) => {
      return items.sort((a, b) => {
        const aInStock = a?.extension_attributes?.in_stock === 1;
        const bInStock = b?.extension_attributes?.in_stock === 1;
        return aInStock === bInStock ? 0 : aInStock ? 1 : -1;
      });
    };

    return {
      groceries: sortItemsByStock(categorizedItems?.groceries || []),
      gifts: sortItemsByStock(categorizedItems?.gifts || []),
    };
  }, [cartItems]);

  const outOfStockItemIds = useMemo(() => {
    const allItems = [
      ...(updatedCartItems?.groceries || []),
      ...(updatedCartItems?.gifts || []),
    ];

    return allItems
      .filter(item => {
        const ext = item?.extension_attributes;
        if (!ext) return false; // skip check if extension_attributes missing
        return ext.in_stock !== 1;
      })
      .map(item => item.item_id);
  }, [updatedCartItems]);

  const freebieExists = useMemo(
    () =>
      cartItems &&
      !!cartItems?.find(
        item => item?.extension_attributes?.product_remark === 'freebie'
      ),
    [cartItems]
  );

  const handleOpenPickupStore = () => {
    setShowDeliveryModal(true);
    toggleCartSidebar(false);
  };
  const cartItemsArray = Array.from(cartItemsMap.values());

  const allInStock = cartItemsArray.every(item => {
    const ext = item?.extension_attributes;
    if (!ext) return true; // Skip check if no extension_attributes
    return ext.in_stock === 1;
  });
  const hasLimitedQty = cartItemsArray.some(item => {
    const ext = item?.extension_attributes;
    if (!ext || !ext.available_qty) return false;
    return ext.available_qty < item.qty;
  });

  function disableCheckout() {
    const cartItemsArray = Array.from(cartItemsMap.values());

    const allInStock = cartItemsArray.every(item => {
      const ext = item?.extension_attributes;
      if (!ext) return true; // Skip check if no extension_attributes
      return ext.in_stock === 1;
    });

    if (!allInStock) {
      setCartDisableErrorMessage(
        'Remove out-of-stock items to continue checkout'
      );
      pushEvent({
        event: 'Cart Error',
        data: {
          message: 'Remove out-of-stock items to continue checkout',
          type: 'OUT_OF_STOCK',
        },
      });
      return;
    }

    const hasLimitedQty = cartItemsArray.some(item => {
      const ext = item?.extension_attributes;
      if (!ext || !ext.available_qty) return false;
      return ext.available_qty < item.qty;
    });

    if (hasLimitedQty) {
      setCartDisableErrorMessage(
        'Some items have limited stock! Please review and adjust quantities to proceed.'
      );
      pushEvent({
        event: 'Cart Error',
        data: {
          message:
            'Some items have limited stock! Please review and adjust quantities to proceed.',
          type: 'OUT_OF_STOCK',
        },
      });
      return;
    }

    if (movDisable && movAmount) {
      const message = `Add items worth ₹${movAmount - cartValue} to unlock home delivery or switch to store pickup`;
      setCartDisableErrorMessage(message);
      pushEvent({
        event: 'Cart Error',
        data: {
          message,
          type: 'MOV',
        },
      });
      return;
    }

    if (
      !deliveryType ||
      (storage.get('serviceable') === 'false' && deliveryType === 'home')
    ) {
      setCartDisableErrorMessage('Please select location to proceed');
      return;
    }

    // All good
    setCartDisableErrorMessage(undefined);
  }

  function cleverTapCheckoutStarted() {
    const skus = getCartSkus(cartItems);
    const hasPurchased = localStorage.getItem('ga4_has_purchased') === '1';
    pushEvent({
      event: 'Checkout Started',
      data: {
        ['Timestamp']: new Date(),
        ['Cart Contents']: skus,
        ['Cart Total Items']: cartItems?.length,
        ['Cart Amount']: cartValue,
        ['Checkout Step']: 'Initiated',
        ['Delivery Type']: deliveryType === 'home' ? 'Home Delivery' : 'Pickup',
        user_type: hasPurchased ? 'repeat' : 'new',
        stage: 'conversion',
        items: JSON.stringify(
          cartItems?.map(item => ({
            sku: item.sku,
            name: item.name,
            qty: item.qty,
            price: item.price || 0,
          })) || []
        ),
      },
    });

    // Google Analytics 4 - begin_checkout event
    if (cartItems && cartItems.length > 0 && cartValue) {
      // Format cart items for GA4
      const formattedItems = cartItems
        .filter(item => {
          // Filter out gift box items that are not the main gift box
          const ext = item?.extension_attributes;
          if (
            ext?.gift_box_item_id &&
            ext?.is_gift_box !== '1' &&
            ext?.is_greeting_card !== 1
          ) {
            return false;
          }
          return true;
        })
        .map(item => ({
          sku: item.sku,
          name: item.name,
          price: item.price || 0,
          weight: item.extension_attributes?.weight,
          weight_unit: item.extension_attributes?.weight_unit,
          qty: item.qty,
        }));

      // Determine checkout category
      const checkoutCategory =
        deliveryType === 'home' ? 'home-delivery' : 'pickup-from-store';

      // Push begin_checkout event
      const dataLayer = window.dataLayer || [];
      dataLayer.push({ ecommerce: null });

      const itemsForGA4 = formattedItems.map((item, index) => ({
        item_id: item.sku,
        item_name: item.name,
        affiliation: 'Google Merchandise Store',
        index: index,
        item_brand: 'Foodstories',
        item_category: 'Food',
        item_variant: `${item.weight || ''} ${item.weight_unit || ''}`.trim(),
        price: item.price,
        quantity: item.qty,
      }));

      dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'INR',
          value: cartValue,
          items: itemsForGA4,
        },
        checkout_category: checkoutCategory,
      });
    }
  }
  const lat = storage.get('lat');
  const lng = storage.get('lng');
  useEffect(() => {
    fetchExpressStandardMethodsCheck();
  }, [lat, lng]);

  const fetchExpressStandardMethodsCheck = async () => {
    const url = `${endpoints.EXPRESS_STANDARD_CHECK_SERVICEABILITY}?latitude=${lat}&longitude=${lng}`;
    const {
      response,
      data,
    }: { response: any; data: { express_standard_delivery: boolean } } =
      await http.get(url);
    if (response.ok) {
      setExpressStandard(data.express_standard_delivery);
    }
  };

  const updateCart = useCallback(async () => {
    const cartId = storage.get('cart_id');
    if (!deliveryMethods?.length) return;
    const defaultMethod =
      deliveryType === 'home'
        ? expressStandard
          ? 'expressstandard'
          : 'flatrate'
        : 'freeshipping';
    const addressObj = {
      addressInformation: {
        shipping_address: shippingAddress,
        billing_address: shippingAddress,
        shipping_carrier_code: defaultMethod, //?.carrier_code,
        shipping_method_code: defaultMethod, //?.method_code,
      },
    };

    try {
      if (!userDetails) {
        const { response } = await http.post(
          `${endpoints.GUEST_CART}/${cartId}/shipping-information`,
          addressObj,
          {
            next: { revalidate: REVALIDATE_TIME },
          }
        );
        if (response?.ok) {
          fetchUpdateCart();
        }
      } else {
        const { response } = await http.post(
          `${endpoints.CUSTOMER_CART}/shipping-information`,
          addressObj,
          {
            next: { revalidate: REVALIDATE_TIME },
          }
        );
        if (response?.ok) {
          fetchUpdateCart();
        }
      }
    } catch (error) {
      logger.error('Error updating cart:', error);
    }
  }, [deliveryMethods, userDetails, stringifiedShippingAddress]);

  const fetchDeliveryMethods = useCallback(
    async (cartId: string) => {
      if (!userDetails && !cartId) return;

      try {
        const url = userDetails
          ? `${endpoints.CUSTOMER_CART}/${endpoints.DELIVERY_METHODS}`
          : `${endpoints.GUEST_CART}/${cartId}/${endpoints.DELIVERY_METHODS}`;

        const {
          response,
          data,
        }: { response: any; data: DeliveryMethodsPropTypes[] } =
          await http.post(url, {
            address: {
              country_id: 'IN',
              postcode: '',
            },
          });
        if (response?.ok) {
          const noPickupMethods = data?.filter(({ method_code }) =>
            storage.get('deliveryType') === 'pickup'
              ? method_code === DeliveryMethodsTypeEnum.FREESHIPPING
              : method_code !== DeliveryMethodsTypeEnum.FREESHIPPING
          );
          setDeliveryMethods(noPickupMethods);
        }
      } catch (e) {
        logger.error(e);
      }
    },
    [userDetails]
  );

  // Combined effect for initial load
  useEffect(() => {
    const cartId = storage.get(USERS_TYPES.guest.id);
    if (
      (userDetails || cartId) &&
      cartItems &&
      cartItems.length > 0 &&
      isInitialLoad
    ) {
      fetchDeliveryMethods(cartId || '');
      setIsInitialLoad(false);
    }
  }, [cartItems, userDetails, fetchDeliveryMethods, isInitialLoad]);

  // Debounced effect for cart updates
  useEffect(() => {
    if (!isInitialLoad && deliveryMethods && deliveryMethods.length > 0) {
      // Clear any existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // Set new timeout
      updateTimeoutRef.current = setTimeout(() => {
        updateCart();
      }, 500); // 500ms debounce
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [deliveryMethods, updateCart, isInitialLoad]);

  useEffect(() => {
    fetchSlabFreeShipping();
  }, []);

  const shippingAmount = paymentDetails?.others?.find(
    ({ code, value }: Payment) => code.toLowerCase() === 'shipping' && value
  )?.value;
  const isHomeDeliveryFree = !shippingAmount && deliveryType === 'home';
  const store_code = storage.get('store_code');
  const slabFreeShippingStoreData = slabFreeShipping?.find(
    (item: ShippingConfig) => item.store_code === store_code
  );
  const isGuest = !storage.get('token');

  const getCheck = () => {
    let check = false;
    if (isGuest && store_code === HYDRABAD_STORE_CODE) {
      check = true;
    } else if (slabFreeShippingStoreData?.suffix_id) {
      if (slabFreeShippingStoreData?.is_special_free_shipping_enabled) {
        if (slabFreeShippingStoreData?.custom_shipping_rates.length > 0) {
          if (deliveryType === 'home' && allInStock && !hasLimitedQty) {
            check = true;
          }
        }
      }
    } else if (!slabFreeShippingStoreData?.suffix_id) {
      if (
        slabFreeShippingStoreData &&
        slabFreeShippingStoreData?.custom_shipping_rates.length > 0
      ) {
        if (deliveryType === 'home' && allInStock && !hasLimitedQty) {
          check = true;
        }
      }
    }
    return check;
  };

  const isNewUIEnable = getCheck();

  useEffect(() => {
    disableCheckout();
  }, [cartItems, movDisable, defaultValue, serviceableString]);

  useEffect(() => {
    if (hasTrackedCartView || !cartItems?.length || !isSlabShippingLoaded)
      return;
    pushEvent({
      event: 'Cart Viewed',
      data: {
        stage: 'intent',
        cart_value: cartValue,
        items_count: cartItems.length,
        'Progress Bar Shown': isNewUIEnable ? 'True' : 'False',
        items: JSON.stringify(
          cartItems.map(item => ({
            sku: item.sku,
            name: item.name,
            qty: item.qty,
            price: item.price || 0,
          }))
        ),
      },
    });
    setHasTrackedCartView(true);
  }, [
    cartItems,
    hasTrackedCartView,
    pushEvent,
    isSlabShippingLoaded,
    isNewUIEnable,
  ]);

  const handleRemoveOutOfStockItems = async () => {
    setLoading(true);
    const cartId = storage.get('cart_id');
    if (!userDetails && !cartId) return;
    try {
      await http.post(
        userDetails?.firstname
          ? `carts/mine/items/remove`
          : `guest-carts/${cartId}/items/remove`,
        { item_ids: outOfStockItemIds }
      );
      // Force a refresh of the cart data
      fetchUpdateCart();
      // Clear the out of stock items from local state
      setCartDisableErrorMessage(undefined);
    } catch (error) {
      logger.error('Error removing out of stock items:', error);
    } finally {
      setLoading(false);
    }
  };

  async function fetchSlabFreeShipping() {
    try {
      const { data, response } = await http.get('shipping/slab-free-shipping');
      if (response.ok) {
        setSlabFreeShipping(data);
      }
    } catch (err) {
      logger.log(err);
    } finally {
      setIsSlabShippingLoaded(true);
    }
  }

  if (!cartItems) {
    return (
      <div className='mt-6 space-y-4 px-3'>
        <Loading />
        <Loading />
        <Loading />
      </div>
    );
  }

  if (cartItems?.length === 0) {
    return (
      <div aria-label='empty-cart' className='space-y-4 px-6 py-12 text-center'>
        <Heading className='text-2xl'>Your Cart is Empty</Heading>
        <Text>
          Or rather, for now it is. So let's fill it with joy-giving things,
          shall we?
        </Text>
        <Button
          label='Shop Your Favourite Foods'
          onClick={() => {
            router.push('/');
            toggleCartSidebar(false);
          }}
        />
      </div>
    );
  }

  if (cartItems?.length && cartItems.length > 0) {
    return (
      <div className='flex flex-col w-full h-full'>
        <div
          className={twMerge(
            'overflow-x-hidden overflow-y-auto max-w-full flex-1'
          )}>
          {/* <div className='px-3 bg-white'>
            <ProgressBarSection />
          </div> */}

          <div className='items-start bg-white py-6'>
            <Text className='mb-3 text-xs font-semibold text-[#a1a0a1] md:text-sm'>
              DELIVERY TYPE
            </Text>
            <div className='relative px-3 max-lg:flex-1'>
              {deliveryType === 'home' &&
              storage.get('serviceable') === 'true' ? (
                <div className='flex flex-row items-center justify-between rounded-lg border border-[#ecbcbf] p-3 text-left'>
                  <div className='flex w-full flex-col gap-0.5 pr-7'>
                    <Text className='px-0 text-xs font-semibold text-[#A0383F] md:text-sm'>
                      HOME DELIVERY
                    </Text>
                    <Text className='mt-1 px-0 text-xs font-semibold md:text-sm'>
                      {storage.get('addressTitle')}
                    </Text>
                    <Text className='px-0 text-[10px] md:text-xs'>
                      {storage.get('addressDescription')}
                    </Text>
                  </div>
                  <button
                    onClick={handleOpenPickupStore}
                    className='flex flex-row items-center justify-center gap-2'>
                    <Image src={EditIcon} alt='icon' className='' />
                  </button>
                </div>
              ) : deliveryType === 'pickup' ? (
                <div className='flex flex-row items-center justify-between rounded-lg border border-[#ecbcbf] p-3 text-left'>
                  <div className='flex w-full flex-col gap-0.5 pr-7'>
                    <Text className='px-0 text-xs font-semibold text-[#A0383F] md:text-sm'>
                      PICKUP FROM STORE
                    </Text>
                    <Text className='mt-1 px-0 text-xs font-semibold md:text-sm'>
                      {storage.get('storeTitle')}
                    </Text>
                    <Text className='px-0 text-[10px] md:text-xs'>
                      {storage.get('storeDescription')}
                    </Text>
                  </div>
                  <button
                    onClick={handleOpenPickupStore}
                    className='flex flex-row items-center justify-center gap-2'>
                    <Image src={EditIcon} alt='icon' className='' />
                  </button>
                </div>
              ) : (
                <div className='flex flex-col items-start justify-start rounded-lg border border-[#ecbcbf] p-3 text-left'>
                  <div className='flex items-center w-full gap-0.5'>
                    <Svg className='h-5 w-5 stroke-[#A0383F] md:h-6 md:w-6 mt-[3px] md:mt-[5px]'>
                      <LocationPinIcon />
                    </Svg>
                    <Text className='px-0  md:text-base font-semibold text-[#38373A] text-sm'>
                      Please select location to proceed{' '}
                    </Text>
                  </div>
                  <div className='text-[10px] md:text-xs text-[#38373A] font-medium mt-1'>
                    Choose your preferred delivery method to ensure a smooth and
                    convenient experience.
                  </div>
                  <Button
                    label='SELECT LOCATION'
                    className='mt-4 md:w-[50%] w-[55%]'
                    onClick={() => setShowDeliveryModal(true)}
                  />
                </div>
              )}
            </div>
          </div>
          {outOfStockItemIds?.length > 0 && (
            <div className='space-y-3 bg-[#FAF9F9] pb-3 pt-5 px-3'>
              <div className='rounded-lg bg-white  py-3 flex flex-col gap-2'>
                <Text className='text-[13px] md:text-sm text-[#A0383F]'>
                  Multiple items in your cart are out of stock. Please remove
                  Out of stock items to continue
                </Text>
                <div
                  className='text-[13px] md:text-sm text-[#A0383F] underline font-semibold cursor-pointer px-3'
                  onClick={handleRemoveOutOfStockItems}>
                  Remove all
                </div>
              </div>
            </div>
          )}
          {/* <FreeProductCard locked/> */}
          <div className='space-y-3 bg-[#FAF9F9] pb-6 pt-2' aria-label='types'>
            {cartItems && cartItems?.length > 0 && (
              <>
                {updatedCartItems?.groceries?.length > 0 && (
                  <CartProductItem
                    label='GROCERIES'
                    items={updatedCartItems?.groceries}
                    layout='cart'
                  />
                )}
                {updatedCartItems?.gifts?.length > 0 && (
                  <CartProductItem
                    label='GIFTS'
                    items={updatedCartItems?.gifts}
                    layout='cart'
                  />
                )}
              </>
            )}
          </div>
          {freebieExists && (
            <div className='-mt-3 mb-3 rounded-md px-3'>
              <Text className='rounded-md bg-white px-2 py-2 pl-3 font-semibold text-fig'>
                {process.env.NEXT_PUBLIC_FREEBIE_MESSAGE ??
                  'Enjoy a complimentary treat from us!'}
              </Text>
            </div>
          )}
          <div className='space-y-6 bg-white py-6'>
            <div>
              <CartRecommendations
                toggleCartSidebar={toggleCartSidebar}
                loading={loadingRecommended}
              />
            </div>

            <div className='px-3'>
              <OrderSummary
                itemsCount={cartItems?.length ?? 0}
                paymentDetails={paymentDetails}
                isLoading={cartIsLoading}
              />
            </div>
            <div className='px-6'>
              <PolicySection
                classes={{ list: { root: 'list-disc ml-3' } }}
                heading='Returns & Exchange Policy'
                list={RETURN_EXCHANGE_POLICY}
                toggleCartSidebar={toggleCartSidebar}
              />
            </div>
            <div className='w-full text-center'>
              <Button
                onClick={() => toggleCartSidebar(false)}
                variant='link'
                className='btn link-btn px-6 text-sm'
                label='Continue Shopping'
              />
            </div>
          </div>
        </div>
        <div
          className={twMerge(
            'z-10 w-full bg-white drop-shadow-[0_2rem_2rem_rgba(0,0,0,0.7)]',
            movDisable && 'rounded-t-lg'
          )}>
          {isNewUIEnable ? (
            <DeliveryProgress
              custom_shipping_rates2={
                slabFreeShippingStoreData?.custom_shipping_rates
              }
            />
          ) : (
            cartDisableErrorMessage && (
              <div className='rounded-t-lg bg-fig py-1'>
                <Text className='text-center text-sm font-medium text-white'>
                  {cartDisableErrorMessage}
                </Text>
              </div>
            )
          )}
          <div
            aria-label='sidebar-footer'
            className={twMerge('p-3', isNewUIEnable ? 'flex' : 'space-y-2')}>
            {!isNewUIEnable && (
              <div className='flex items-center justify-between gap-3'>
                <Text className='text-base font-semibold'>Grand Total</Text>
                {!cartIsLoading ? (
                  <Text className='text-base font-semibold'>
                    ₹{paymentDetails?.total?.value}
                  </Text>
                ) : (
                  <Loader />
                )}
              </div>
            )}
            {isNewUIEnable && (
              <div className='flex-1 flex'>
                <div className='flex flex-col justify-center w-full'>
                  <div className='flex gap-3 items-center w-[85%]'>
                    <Text className='text-xs md:text-base px-0'>
                      Sub Total:{' '}
                    </Text>
                    {!cartIsLoading ? (
                      <Text className='text-lg md:text-xl font-semibold px-0'>
                        ₹{cartValue}
                      </Text>
                    ) : (
                      <div className='h-5 w-[60px] bg-gray-200 animate-pulse rounded-full' />
                    )}
                  </div>

                  <div className='flex gap-0.5 items-center'>
                    <Text className='text-xs px-0 text-[#85868B]'>
                      Shipping Fee:{' '}
                    </Text>
                    {!cartIsLoading ? (
                      <Text className='text-xs px-0 text-[#85868B] text-[10px] md:text-xs ml-1 md:ml-1.5'>
                        <span
                          className={twMerge(
                            'text-[#85868B] text-[10px] md:text-xs',
                            isHomeDeliveryFree && 'line-through'
                          )}>
                          {' '}
                          ₹{isHomeDeliveryFree ? '99' : shippingAmount}
                        </span>
                        {isHomeDeliveryFree && (
                          <span className='text-[#337052] text-xs md:text-sm font-bold'>
                            {' '}
                            Free{' '}
                          </span>
                        )}
                      </Text>
                    ) : (
                      <div className='h-3 w-[30px] bg-gray-200 animate-pulse rounded-full' />
                    )}
                  </div>
                </div>
              </div>
            )}

            <Button
              disabled={
                !!cartDisableErrorMessage ||
                !storage.get('deliveryType') ||
                (storage.get('serviceable') === 'false' &&
                  deliveryType === 'home')
              }
              label={isNewUIEnable ? 'Checkout' : 'CHECKOUT'}
              onClick={onSubmit}
              className='px-[1.88rem] py-[0.94rem] flex-1 font-medium'
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SectionCart;
